import React, { useMemo } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import StatsCardGroup from '@/components/school-management/StatsCard/StatsCardGroup';
import type { StatsCardProps } from '@/components/school-management/StatsCard/StatsCard';
import { useUserStatistics } from '@/school-management/hooks/useUserStatistics';
import { buildUserStatisticsPayload } from '@/school-management/utils/userStatisticsFilters';
import { formatMinutes } from '@/utils/formatters';
import type { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { UserState } from '@/types/user';
import schoolDetailsStyles from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import userUtils from '@/utils/user';

const calculatePercentage = (value: number, total: number): string => {
  if (total <= 0 || value <= 0) return '';
  return `${Math.floor((value / total) * 100)}%`;
};

export interface TeacherMyClassesStatisticsProps {
  user: UserState | null;
  classFilters: Record<string, FilterValue>;
  hasAnyClassFilter: boolean;
}

/**
 * Same metrics cards as admin school “Classes” tab, scoped like my-classes filters
 * (class / grade + logged-in teacher).
 */
const TeacherMyClassesStatistics: React.FC<TeacherMyClassesStatisticsProps> = ({
  user,
  classFilters,
  hasAnyClassFilter,
}) => {
  const { t } = useAdminTranslation();
  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const teacherUserId = user?.id;
  const isAdmin = userUtils.isAdmin(user);

  const statsPayload = useMemo(
    () =>
      buildUserStatisticsPayload({
        activeTab: 'classes',
        classFilters,
        classesTabLockedTeacherIds: teacherUserId ? [teacherUserId] : undefined,
        t,
      }),
    [teacherUserId, classFilters, t]
  );

  const { data: userStats, loading: statsLoading } = useUserStatistics(
    !!organizationId && !!teacherUserId,
    statsPayload
  );

  const hasRegisteredUsers = (userStats?.registeredStudents ?? 0) > 0;

  const hasNoDataForFilters =
    hasAnyClassFilter && !statsLoading && (userStats?.registeredStudents ?? 0) === 0;
  const emptyMessageForCards = hasNoDataForFilters
    ? t('schoolDetailsPage.students.zeroState.filterTitle') || ''
    : null;

  const stats: StatsCardProps[] = useMemo(
    () => [
      ...(isAdmin
        ? [
            {
              value: userStats?.engagedTeachers ?? 0,
              label: t('statsCardGroup.engagedTeachers'),
              tooltip: t('statsCardGroup.engagedTeachersTooltip') || '',
              emptyMessage:
                emptyMessageForCards ?? (t('statsCardGroup.engagedTeachersEmpty') || ''),
              isLoading: statsLoading,
            },
          ]
        : [
            {
              value: userStats?.passiveStudents ?? 0,
              label: t('statsCardGroup.passiveStudents'),
              tooltip: t('statsCardGroup.passiveStudentsTooltip') || '',
              emptyMessage:
                emptyMessageForCards ?? (t('statsCardGroup.passiveStudentsEmpty') || ''),
              disableFallback: hasRegisteredUsers,
              isLoading: statsLoading,
            },
          ]),
      {
        value: userStats?.engagedStudents ?? 0,
        label: t('statsCardGroup.engagedStudents'),
        tooltip: t('statsCardGroup.engagedStudentsTooltip') || '',
        emptyMessage: emptyMessageForCards ?? (t('statsCardGroup.engagedStudentsEmpty') || ''),
        disableFallback: hasRegisteredUsers,
        isLoading: statsLoading,
      },
      {
        value:
          userStats != null && typeof userStats.totalTimeSpent === 'number'
            ? formatMinutes(userStats.totalTimeSpent)
            : '0m 0s',
        label: t('statsCardGroup.totalTimeSpent'),
        tooltip: t('statsCardGroup.totalTimeSpentTooltip') || '',
        isTimeValue: true,
        isTimeCard: true,
        labelIcon: 'clock',
        emptyMessage: emptyMessageForCards ?? (t('statsCardGroup.totalTimeSpentEmpty') || ''),
        disableFallback: hasRegisteredUsers,
        isLoading: statsLoading,
      },
      {
        value:
          userStats != null && typeof userStats.averageTimeSpent === 'number'
            ? formatMinutes(userStats.averageTimeSpent)
            : '0m 0s',
        label: t('statsCardGroup.averageSessionTime'),
        tooltip: t('statsCardGroup.averageSessionTimeTooltip') || '',
        isTimeValue: true,
        isTimeCard: true,
        emptyMessage: emptyMessageForCards ?? (t('statsCardGroup.averageSessionTimeEmpty') || ''),
        disableFallback: hasRegisteredUsers,
        isLoading: statsLoading,
      },
    ],
    [userStats, statsLoading, emptyMessageForCards, hasRegisteredUsers, t]
  );

  return (
    <div className={schoolDetailsStyles.statsSection}>
      <StatsCardGroup stats={stats} variant='row4' />
    </div>
  );
};

export default TeacherMyClassesStatistics;
